import { Text, View, ScrollView, RefreshControl, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useState, useEffect, useMemo, useRef } from 'react';
import useGuide from '@/utils/hooks/useGuide';
import GuideTarget from '@/components/Guides/GuideTarget';
import { useMutation, useQueryClient, useIsRestoring } from '@tanstack/react-query';
import { Redirect, useIsFocused } from 'expo-router';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import TaskCard from '@/components/Tasks/TaskCard';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import PagerCard from '@/components/General/PagerCard';
import SmallGroupCard from '@/components/SmallGroups/SmallGroupCard';
import Unicon from '@/components/General/Unicon';
import TrainingClassCard from '@/components/Dashboard/TrainingClassCard';
import ShortcutsSection from '@/components/Dashboard/ShortcutsSection';
import DashboardGrid from '@/components/Dashboard/DashboardGrid';
import RecentlyVisitedSection from '@/components/Dashboard/RecentlyVisitedSection';
import DashboardEventsSection from '@/components/Dashboard/DashboardEventsSection';
import MyBoardsFeedSection from '@/components/Dashboard/MyBoardsFeedSection';
import ActiveCareSection from '@/components/Dashboard/ActiveCareSection';
import DashboardSettingsModal from '@/components/Dashboard/DashboardSettingsModal';
import ShortcutsManagerModal from '@/components/Dashboard/ShortcutsManagerModal';
import DeferredUntilFocused from '@/components/General/DeferredUntilFocused';
import useRenderProbe from '@/utils/hooks/useRenderProbe';
import { ConnectedReactionMenu } from '@/components/Connect/ReactionMenu';
import OrganizationSetupCard from '@/components/Dashboard/OrganizationSetupCard';
import FreeSetupDashboard from '@/components/Dashboard/FreeSetupDashboard';
import { canManageOrganizationSetup, setupProgress } from '@/config/organizationSetup';
import useOrganizationSetup from '@/utils/hooks/useOrganizationSetup';
import { addPostReaction, removePostReaction } from '@/data/connect';
import { DEFAULT_DASHBOARD_SECTIONS } from '@/data/settings';

const SECTION_CONFIG = {
    recentlyVisited: { title: "Recently Visited", icon: "time-outline", emptyText: "No recently visited items" },
    mySmallGroups: { title: "My Small Groups", feature: "groups", emptyText: "No Small Groups Yet!", linkRoute: "/smallgroups", linkText: "View All" },
    tasksNeedingAssigned: { title: "Tasks to Assign", feature: "tasks", emptyText: "No tasks needing assigned!", linkRoute: "/tasks/schedule", linkText: "Schedule" },
    myUpcomingTasks: { title: "My Upcoming Tasks", feature: "tasks", emptyText: "No tasks for the next 7 days!" },
    upcomingEvents: { title: "Events", feature: "events", emptyText: "No events on this date", linkRoute: "/events", linkText: "Calendar" },
    myTraining: { title: "My Training", feature: "training", emptyText: "No upcoming training classes!", linkRoute: "/training", linkText: "View All" },
    myBoardsFeed: { title: "My Boards Feed", icon: "newspaper-outline", emptyText: "No posts from your boards yet!", linkRoute: "/connect/boards/feed", linkText: "View All" },
    // Rendered by ActiveCareSection — its own gated, bounded query.
    activeCare: { title: "Active Care", icon: "heart-outline" },
};

export default function HomeScreen() {
    return <DeferredUntilFocused><DashboardScreen /></DeferredUntilFocused>;
}

function DashboardScreen() {
    const { entitlements } = useOrganizationEntitlements();
    const { data: user } = Query.people.loggedInUser();
    if (isFreeOrg(entitlements)) {
        return canManageOrganizationSetup(user)
            ? <FreeSetupDashboard user={user} />
            : <Redirect href="/(tabs)/(home)/people" />;
    }
    return <PaidHomeScreen />;
}

function PaidHomeScreen() {
    if (__DEV__) { (globalThis.__renderCounts ||= {}).paidHome = ((globalThis.__renderCounts.paidHome || 0) + 1); }
    const [loggedInUserRevalidated, setLoggedInUserRevalidated] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
    const [smallGroupFilter, setSmallGroupFilter] = useState('All');

    const guideScrollRef = useRef(null);
    const queryClient = useQueryClient();
    const { data: loggedInUser, isLoading: isLoadingUserInfo, isFetching: isFetchingUserInfo } = Query.people.loggedInUser();
    const { data: organizationSetup } = useOrganizationSetup(loggedInUser);
    const setup = setupProgress(organizationSetup);
    // Free orgs never mount this screen (shared layout redirects to People);
    // the enabled gate covers the mid-session downgrade frame so the paid
    // dashboard RPC is never issued for a Free org.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const { data: dashboardData, isLoading: isLoadingDashboardData, isFetching: isFetchingDashboardData } = Query.dashboardData.byPersonId({ personID: loggedInUser?.id, options: { enabled: !!loggedInUser?.id && !isFree } });
    const isRestoring = useIsRestoring();
    // Single per-widget loading flag. isRestoring covers persist-hydration
    // frames (queries pending+idle, isLoading false, no data yet); the last
    // clause covers the enabled-gate flip frame (personID/isFree just became
    // truthy, query pending but fetch not started). When loggedInUser is
    // absent the flag falls back to the query flags so widgets don't spin
    // forever on error paths.
    const dashboardDataLoading = isRestoring || isLoadingUserInfo || isLoadingDashboardData || (!!loggedInUser?.id && !isFree && !dashboardData);

    const upcomingTasks = dashboardData?.upcomingTasks;
    const upcomingTaskAssignments = dashboardData?.upcomingTaskAssignments;
    const allSmallGroups = dashboardData?.smallGroups;
    const shortcuts = dashboardData?.shortcuts;
    const upcomingTrainingClasses = dashboardData?.upcomingTrainingClasses;
    const sections = { ...DEFAULT_DASHBOARD_SECTIONS, ...dashboardData?.sections };
    useGuide('dashboard.basics', { ready: !dashboardDataLoading });

    // Boards feed reaction menu — rendered at the screen root (sibling of ScrollView)
    // so ReactionMenu's absolute positioning maps window coords correctly on native.
    // If rendered inside the dashboard ScrollView/card, absolute offsets stack against
    // the nested parent and the menu flies off-screen.
    const isFocused = useIsFocused();
    useRenderProbe(isFocused ? 'dashboard-focused' : 'dashboard-blurred', {
        isFocused, loggedInUser, organizationSetup, entitlements, dashboardData,
        isFetchingUserInfo, isFetchingDashboardData, isRestoring, dashboardDataLoading,
        settingsModalOpen, shortcutsModalOpen, smallGroupFilter, loggedInUserRevalidated,
    });

    const updatePostInQueryCache = (postID, updater) => {
        queryClient.setQueryData(QueryKeys.posts.myBoardsFeed(), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o?.id == postID ? updater(o) : o)}))}
        })
    }

    const addReactionMutation = useMutation({
        mutationFn: addPostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions || []), {...data, Person: loggedInUser}]}))
        }
    })

    const removeReactionMutation = useMutation({
        mutationFn: removePostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions?.filter(r => !(r.postID == data.postID && r.personID == data.personID)) || [])]}))
        }
    })

    const updateReaction = async (post, name) => {
        const currentUserReaction = post?.Reactions?.find(r => r.Person?.id == loggedInUser?.id)

        if(currentUserReaction?.reaction == name){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
            return
        }

        if(currentUserReaction){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
        }

        await addReactionMutation.mutateAsync({postID: post.id, reaction: name, personID: loggedInUser?.id})
    }

    useEffect(() => {
        if (!isFetchingUserInfo) {
            if (!loggedInUser || !loggedInUser?.id || !loggedInUser?.firstName) {
                if (!loggedInUserRevalidated) {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
                    setLoggedInUserRevalidated(true);
                }
            }
        }
    }, [isFetchingUserInfo]);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardData.byPersonId(loggedInUser?.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.boards.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.posts.myBoardsFeed() });
    };

    const openShortcutsManager = () => {
        setSettingsModalOpen(false);
        setTimeout(() => setShortcutsModalOpen(true), 300);
    };

    const chunkItems = (items, size = 2) => {
        const pages = [];
        for (let i = 0; i < items?.length; i += size) {
            pages.push(<View key={i}>{items.slice(i, i + size)}</View>);
        }
        return pages;
    };

    const sortByNearestDate = (a, b) => {
        const aDate = new Date(a.dueDate || a.EventInstance?.startTime || 0);
        const bDate = new Date(b.dueDate || b.EventInstance?.startTime || 0);
        return aDate - bDate;
    };

    const upcomingTaskItems = dashboardDataLoading ? null : [...(upcomingTasks || [])].sort(sortByNearestDate).slice(0, 10).map(t => <TaskCard key={t.id} taskInstance={t} showEvent fromScreen="/" variant={'alternate'} />);
    const taskAssignItems = dashboardDataLoading ? null : [...(upcomingTaskAssignments || [])].sort(sortByNearestDate).slice(0, 10).map(t => <TaskCard key={t.id} taskInstance={t} showEvent fromScreen="/" variant={'alternate'} />);

    const smallGroupFilterOptions = useMemo(() => {
        const options = ['All'];
        const seen = new Set();
        allSmallGroups?.forEach(s => {
            const name = s?.SmallGroupType?.name;
            if (name && !seen.has(name)) {
                seen.add(name);
                options.push(name);
            }
        });
        return options;
    }, [allSmallGroups]);

    const smallGroupItems = dashboardDataLoading ? null : (smallGroupFilter === 'All' ? allSmallGroups : allSmallGroups?.filter(s => s?.SmallGroupType?.name === smallGroupFilter))?.slice(0, 10).map(s => <SmallGroupCard key={s.id} smallGroup={s} variant={'alternate'} />);
    const trainingItems = dashboardDataLoading ? null : upcomingTrainingClasses?.slice(0, 10).map(t => <TrainingClassCard key={t.id} instance={t} />);

    const sectionData = useMemo(() => ({
        mySmallGroups: { pages: chunkItems(smallGroupItems), loading: dashboardDataLoading, linkRouteParams: {}, filterOptions: smallGroupFilterOptions, activeFilter: smallGroupFilter, onFilterChange: setSmallGroupFilter },
        tasksNeedingAssigned: { pages: chunkItems(taskAssignItems), loading: dashboardDataLoading, linkRouteParams: {} },
        myUpcomingTasks: { pages: chunkItems(upcomingTaskItems), loading: dashboardDataLoading, linkRoute: "/people/" + loggedInUser?.id + "?tab=tasks", linkText: "All My Tasks", linkRouteParams: {} },
        myTraining: { pages: chunkItems(trainingItems), loading: dashboardDataLoading, linkRouteParams: {} },
    }), [smallGroupItems, taskAssignItems, upcomingTaskItems, trainingItems, smallGroupFilterOptions, smallGroupFilter, dashboardDataLoading]);

    const visibleSections = Object.entries(sections)
        .filter(([_key, config]) => config.visible)
        .filter(([key]) => {
            if (key === 'organizationSetup') return canManageOrganizationSetup(loggedInUser) && setup.total > 0 && !setup.complete;
            if (key === 'mySmallGroups' && !loggedInUser?.can?.('View Small Groups')) return false;
            if (key === 'upcomingEvents' && !loggedInUser?.can?.('View Events')) return false;
            if (key === 'myTraining' && !loggedInUser?.can?.('View Training')) return false;
            // §25.9 — Active care is permission-scoped ('View Follow Up');
            // its section query is separately team-gated server-side.
            if (key === 'activeCare' && !loggedInUser?.can?.('View Follow Up')) return false;
            return true;
        })
        .sort((a, b) => a[1].order - b[1].order)
        .map(([key]) => key);

    const currentHour = new Date().getHours();
    const greeting = currentHour < 12 ? "Good Morning" : currentHour < 18 ? "Good Afternoon" : "Good Evening";

    // Belt-and-suspenders with the shared-layout route gate: if a Free plan
    // lands while this screen is focused, leave the dashboard immediately.
    if (isFree) {
        return <Redirect href="/(tabs)/(home)/people" />;
    }

    return (
        <Animated.View style={styles.root}>
            <ScrollView
                ref={guideScrollRef}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.scrollContent}
                scrollEventThrottle={16}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isFetchingUserInfo || isFetchingDashboardData} onRefresh={refresh}/>}
            >
                {/* ── Greeting Bar ── */}
                <Animated.View entering={FadeInDown.duration(350).delay(50)} style={styles.greetingBar}>
                    <GuideTarget id="dashboard.overview" style={styles.greetingTextContainer} scrollRef={guideScrollRef}>
                        <Text style={styles.greetingText} numberOfLines={2} ellipsizeMode="tail">{greeting}{(!isLoadingUserInfo && loggedInUser?.firstName) ? ", " + loggedInUser?.firstName : ""}!</Text>
                        <Text style={styles.greetingSubtext}>Here&apos;s your overview</Text>
                    </GuideTarget>
                    <View style={{ gap: 8, alignItems: 'flex-end' }}>
                        <GuideTarget id="dashboard.customize" scrollRef={guideScrollRef} activate={() => setSettingsModalOpen(true)}>
                            <Pressable testID="dashboard-options" style={styles.settingsBtn} onPress={() => setSettingsModalOpen(true)}>
                                <Unicon name="options-outline" size={20} colorVariant="textSecondary" />
                            </Pressable>
                        </GuideTarget>
                    </View>
                </Animated.View>

                {/* ── Shortcuts ── */}
                <Animated.View entering={FadeInDown.duration(350).delay(120)} style={styles.contentSection}>
                    <GuideTarget id="dashboard.shortcuts" scrollRef={guideScrollRef}>
                        <ShortcutsSection
                            shortcuts={shortcuts || []}
                            isLoading={dashboardDataLoading}
                            onAddShortcut={() => setShortcutsModalOpen(true)}
                        />
                    </GuideTarget>
                </Animated.View>

                {/* ── Dashboard Cards ── */}
                <Animated.View entering={FadeInDown.duration(350).delay(180)} style={styles.contentSection}>
                <DashboardGrid>
                    {visibleSections.map((key) => {
                        if (key === 'organizationSetup') {
                            return <OrganizationSetupCard key={key} user={loggedInUser} />;
                        }
                        const config = SECTION_CONFIG[key];
                        const data = sectionData[key];
                        if (!config) return null;

                        // Recently Visited is a special case - render the section directly
                        if (key === 'recentlyVisited') {
                            return <RecentlyVisitedSection key={key} />;
                        }

                        // Events is a special case - selected date controls the list
                        if (key === 'upcomingEvents') {
                            return <DashboardEventsSection key={key} />;
                        }

                        // My Boards Feed is a special case - infinite scroll vertical list
                        if (key === 'myBoardsFeed') {
                            return <MyBoardsFeedSection key={key} />;
                        }

                        // Active Care is a special case - its own bounded,
                        // permission-gated episode query (§25.9).
                        if (key === 'activeCare') {
                            return <ActiveCareSection key={key} />;
                        }

                        return (
                            <PagerCard
                                key={key}
                                testID={`dashboard-section-${key}`}
                                title={config.title}
                                icon={config.icon}
                                feature={config.feature}
                                pages={data.loading ? null : data.pages}
                                isLoading={data.loading}
                                emptyText={config.emptyText}
                                linkRoute={data.linkRoute || config.linkRoute}
                                linkText={data.linkText || config.linkText}
                                linkRouteParams={data.linkRouteParams}
                                filterOptions={data.filterOptions}
                                activeFilter={data.activeFilter}
                                onFilterChange={data.onFilterChange}
                            />
                        );
                    })}
                </DashboardGrid>
                </Animated.View>
            </ScrollView>

            <DashboardSettingsModal
                isOpen={settingsModalOpen}
                onClose={() => setSettingsModalOpen(false)}
                shortcuts={shortcuts || []}
                onManageShortcuts={openShortcutsManager}
                sections={sections}
                loggedInUser={loggedInUser}
            />
            <ShortcutsManagerModal
                isOpen={shortcutsModalOpen}
                onClose={() => setShortcutsModalOpen(false)}
                personID={loggedInUser?.id}
                existingShortcuts={shortcuts || []}
            />
            <ConnectedReactionMenu
                focused={isFocused}
                onReactionPress={(post, name) => updateReaction(post, name)}
                canReply={false}
            />
        </Animated.View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }, { isPortrait }) => ({
    root: {
        flex: 1,
        paddingTop: isPortrait ? 80 : 0,
        backgroundColor: 'transparent',
        width: "100%",
    },
    scrollContent: {
        paddingBottom: isPortrait ? 100 : spacing.xl,
        gap: spacing.lg,
        width: "100%",
    },
    contentSection: {
        width: "100%",
        maxWidth: 1200,
        alignSelf: "center",
    },
    greetingBar: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: isPortrait ? spacing.md : spacing.lg,
        paddingBottom: 0,
        paddingTop: isPortrait ? 0 : spacing.lg,
        maxWidth: 1200,
        alignSelf: "center",
        width: "100%",
    },
    greetingText: {
        fontFamily: fonts.bold,
        fontSize: typography.h1,
        color: colors.text,
        letterSpacing: -0.6,
    },
    greetingSubtext: {
        fontFamily: fonts.body,
        fontSize: typography.caption,
        color: colors.textSecondary,
        marginTop: spacing.xs,
    },
    greetingTextContainer: {
        flex: 1,
        marginRight: spacing.md,
        minWidth: 0,
        overflow: 'hidden',
        _web: {
            overflowWrap: 'break-word',
        },
    },
    settingsBtn: {
        width: 38,
        height: 38,
        borderRadius: 10,
        backgroundColor: colors.surfaceVariant,
        alignItems: "center",
        justifyContent: "center",
        _web: {
            transition: "background-color 0.15s ease",
            _hover: { backgroundColor: colors.hoverStrong },
        },
    },
}));